import React, { Fragment, useState, useEffect, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Box, Grid, Button, FormLabel, TextField, Typography, FormControl } from '@mui/material';
import { Add } from '@mui/icons-material';

// components
import appRoutes from '../../../routes/appRoutes';
import ProtocolList from './ProtocolList';
import SavedTreatmentTable from './SavedTreatmentTable';
import NotesList from './notesList';

// APIs
import {
  getProtocol as getProtocolAPI,
  deleteTreatment,
  completeProtocol,
  deleteProtocolNote,
} from '../../../api/protocols';

import { usePatientWarningCheck } from '../../../hooks/usePatientWarningCheck';
import CONSTANT, { PROTOCOL_TYPE } from '../../../utils/constants/common';
import { useAuth } from '../../../context/AuthContext';
import useToast from '../../../hooks/useToast';
import AddEditTreatmentModal from './AddEditTreatmentModal';

const ViewProtocol = () => {
  let navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { showToast } = useToast();
  const commonReducers = useSelector((state) => state.commonReducers);
  const [openAddTreatmentFormModel, setOpenAddTreatmentFormModel] = useState(false);

  // List of protocols
  const [protocolList, setProtocolList] = useState([]);
  const [protocolId, setProtocolId] = useState('');

  const [protocolType, setProtocolType] = useState(null);
  const [notes, setNotes] = useState([]);
  const [clinicId, setClinicId] = useState('');
  const [doctorId, setDoctorId] = useState('');
  const [presDoc, setPresDoc] = useState('-');
  const [presClinic, setPresClinic] = useState('-');
  const [checkWarning, setCheckWarning] = useState('');
  const [treatmentList, setTreatmentList] = useState([]);
  const [protocolStatus, setProtocolStatus] = useState(CONSTANT.S4_DRUG_PROTOCOL_STATUS.NEW);
  const treatmentIdRef = useRef('');

  const formik = useFormik({
    initialValues: {
      note: '',
    },
    validationSchema: Yup.object({}),
    onSubmit: (values) => {
      completeS4DrugProtocol(values);
      navigate(appRoutes.prProtocols);
    },
  });

  const goToProtocolsPage = () => {
    navigate(appRoutes.prProtocols);
  };

  const getProtocol = async (id) => {
    try {
      setOpenAddTreatmentFormModel(false);
      const params = { protocolId: id || protocolId };
      const res = await getProtocolAPI(params);
      if (res.data?.doctorId) {
        setDoctorId(res.data.doctorId?._id);
        setPresDoc(`${res.data.doctorId.displayFirstName} ${res.data.doctorId.lastName}`);
      }
      if (res.data?.clinicId) {
        setClinicId(res.data.clinicId?._id);
        setPresClinic(res.data.clinicId?.name);
      }
      setProtocolType(res.data.type);
      setProtocolList(res.data.protocols);
      setTreatmentList(res.data.treatments ? res.data.treatments : []);
      setNotes(res.data.notes ? res.data.notes : []);
      setProtocolStatus(res.data.status);
    } catch (error) {
      // continue regardless of error
    }
  };

  const completeS4DrugProtocol = async (values) => {
    try {
      let dataObj = {
        protocolId,
        doctorId: doctorId,
        clinicId: clinicId,
        note: {
          note: values.note,
          addedBy: user?._id,
        },
        patientId: commonReducers.prPatientId,
        // headClinic: props.commonReducers.prPatientData.defaultHeadClinicId._id,
        headClinicId: commonReducers.headClinicData._id,
        createdBy: user?._id,
        type: protocolType === PROTOCOL_TYPE.OTHER ? PROTOCOL_TYPE.OTHER : PROTOCOL_TYPE.S4_DRUG,
      };
      await completeProtocol(dataObj);
      showToast('Protocol updated successfully', 'success');
      setCheckWarning(true);
    } catch (error) {
      showToast('Protocol could not be updated', 'error');
    }
  };

  const deleteS4Treatment = async (treatmentId) => {
    try {
      const dataObj = { protocolId, treatmentId };
      await deleteTreatment(dataObj);
      showToast('Treatment deleted successfully', 'success');
      setCheckWarning(true);
      getProtocol();
    } catch (error) {
      showToast('Treatment could not be deleted', 'error');
    }
  };

  const editS4Treatment = (treatmentId) => {
    treatmentIdRef.current = treatmentId;
    setOpenAddTreatmentFormModel(true);
  };

  useEffect(() => {
    let parts = location.pathname.split('/');
    let protocolIdFromParam = parts[parts.length - 1];
    setProtocolId(protocolIdFromParam);
    getProtocol(protocolIdFromParam);
  }, []);

  const deleteNote = async (noteId) => {
    try {
      const dataObj = {
        protocolId,
        noteId,
      };
      await deleteProtocolNote(dataObj);
      showToast('Protocol Note deleted successfully', 'success');
      const updatedNotes = notes.filter((note) => note._id !== noteId);
      setNotes(updatedNotes);
    } catch (error) {
      showToast('Protocol Note could not be deleted', 'error');
    }
  };

  usePatientWarningCheck(checkWarning);

  return (
    <Fragment>
      <Box component="form" onSubmit={formik.handleSubmit}>
        <Box
          width={1}
          pl={2}
          pt={3}
          pb={2}
          display="flex"
          alignItems="center"
          borderBottom="1px solid #dee2e6">
          <NavLink
            to={appRoutes.prProtocols}
            className="insta-link-orange font-15 "
            style={{ textDecoration: 'none', verticalAlign: 'text-bottom' }}
            href="#">
            <i className="fas fa-arrow-left"></i>
            <span style={{ marginLeft: 4 }} className="back-btn-text">
              Back
            </span>
          </NavLink>
          <Typography component="span" ml={1.5} variant="h4">
            {`View ${protocolType === PROTOCOL_TYPE.OTHER ? 'Other' : 'S4 Drug'} Protocol`}
          </Typography>
        </Box>
        <Box width={1} mt={3} px={3}>
          <Grid container spacing={3} justifyContent="space-between">
            <Grid item sm={5}>
              <Typography>{'Clinic: ' + presClinic}</Typography>
            </Grid>
            {presDoc && (
              <Grid item sm={5}>
                <Typography>{'Prescribing Doctor: Dr ' + presDoc}</Typography>
              </Grid>
            )}
            <Grid item sm={12}>
              <ProtocolList
                protocolType={protocolType}
                protocolList={protocolList}
                noDelete={false}
              />
            </Grid>

            <Box width={1} mt={5} borderBottom="1px solid #dee2e6"></Box>
            <Grid item sm={4}>
              <Typography variant="h4">Treatments</Typography>
            </Grid>
            <Grid item sm={8}>
              <Box textAlign="right">
                <Button
                  startIcon={<Add />}
                  className="heading-btn-width"
                  onClick={() => setOpenAddTreatmentFormModel(true)}
                  disabled={protocolStatus === CONSTANT.S4_DRUG_PROTOCOL_STATUS.EXPIRED}>
                  Add Treatment
                </Button>
              </Box>
            </Grid>
            <Grid item sm={12}>
              <SavedTreatmentTable
                protocolType={protocolType}
                treatments={treatmentList}
                deleteTreatment={(id) => deleteS4Treatment(id)}
                editTreatment={editS4Treatment}
              />
            </Grid>
          </Grid>
        </Box>
        <Box width={1} mt={5} px={3}>
          <Grid container spacing={3}>
            <Grid item sm={6}>
              <FormControl fullWidth>
                <FormLabel>Notes</FormLabel>
                <TextField
                  id="note"
                  name="note"
                  type="text"
                  multiline
                  minRows={4}
                  placeholder=""
                  className="ic-input text-area"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.note}
                />
              </FormControl>
            </Grid>
            <Grid item sm={6}>
              <NotesList notesList={notes} deleteNote={deleteNote} />
            </Grid>
          </Grid>
        </Box>
        <Box width={1} textAlign="center" mt={4}>
          <Button type="submit">Update</Button>
          <Button type="button" sx={{ ml: 2 }} color="secondary" onClick={goToProtocolsPage}>
            Cancel
          </Button>
        </Box>
      </Box>
      <AddEditTreatmentModal
        open={openAddTreatmentFormModel}
        onClose={() => {
          treatmentIdRef.current = '';
          setOpenAddTreatmentFormModel(false);
        }}
        treatmentId={treatmentIdRef.current}
        protocolId={protocolId}
        protocolType={
          protocolType === PROTOCOL_TYPE.OTHER ? PROTOCOL_TYPE.OTHER : PROTOCOL_TYPE.S4_DRUG
        }
        protocolList={protocolList}
        refreshProtocol={getProtocol}
      />
    </Fragment>
  );
};

export default ViewProtocol;

ViewProtocol.propTypes = {
  onFormSubmit: PropTypes.func,
  serverErrorMsg: PropTypes.string,
};
